import { Injectable } from "@angular/core";
import { CouponDetailViewModel, CouponDataModel } from "../../shared/models/coupon.model";
import { LikeService } from "../../services/like.service";
import { CoreLogService } from "../../services/log.service";
import { Observable } from "rxjs";
import { ContentType, ImageOrientation, DateFormatTyps } from "../../shared/contentTypes";
import { RatingService } from "../../services/rating.service";
import DateManager from "../../shared/dateManager";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";
import { CouponRedemptionService } from "../../services/couponRedemption.service";

@Injectable()
export class CouponDetailService {

  private _couponObsServ: IObjectObserver<CouponDataModel>;

  constructor(private _likeService: LikeService,
    private _ratingService: RatingService,
    private _coreLogService: CoreLogService,
    private _couponRedemptionService: CouponRedemptionService,
    objObsInjectHelper: CircularInjectionHelper) {
    this._couponObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<CouponDataModel>(ContentType.Coupon, objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._couponObsServ.ObserveChanges;
  }

  /** Returns the detail view model for the given coupon ID. */
  public GetCouponViewModel(id: number | string): Promise<CouponDetailViewModel> {
    const me = this;
    return new Promise<CouponDetailViewModel>((resolve, reject) => {
      // gets undeleted events
      me._couponObsServ.GetObject(id)
        .then((couponDetail) => {
          me.mapDataModelToDetailViewModel(couponDetail).then(resolve)
            .catch((e) => {
              me._coreLogService.error("CouponDetailService._couponObsServ.GetObject", "mapDataModelToViewModel", e);
              reject();
            });
        })
        .catch((e) => {
          me._coreLogService.error("CouponDetailService._couponObsServ.GetObject", "_couponObsServ.GetObject", e);
          reject();
        });
    });
  }

  /**
  * map a CouponDataModel to EventViewModel
  * @param model Get a CouponDataModel for mapping
  * @result return a EventViewModel
  */
  private async mapDataModelToDetailViewModel(model: CouponDataModel): Promise<CouponDetailViewModel> {
    const me = this;
    if (model) {
      const viewModel = new CouponDetailViewModel();
      viewModel.id = model.id;
      viewModel.momentFromDate = model.date;
      viewModel.startDate = DateManager.MomentAsString(model.date, DateFormatTyps.DDMMYYYY);
      viewModel.endDate = DateManager.MomentAsString(model.end_date, DateFormatTyps.DDMMYYYY);
      viewModel.label_de = model.label_de;
      viewModel.label_en = model.label_en;
      viewModel.text_de = model.text_de.replace("\n", "<br>");
      viewModel.text_en = model.text_en.replace("\n", "<br>");
      viewModel.image_photo = ContentType.Coupon + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
      try {
        viewModel.rate = await me._ratingService.GetRatingStars(ContentType.Coupon, model.id);
      } catch (error) {
        me._coreLogService.error("CouponService.mapDataModelToViewModel", "GetRatingStars try catch", error);
      }
      try {
        viewModel.like = await me._likeService.HasLike(ContentType.Coupon, model.id);
      } catch (error) {
        me._coreLogService.error("CouponService.mapDataModelToViewModel", "HasLike try catch", error);
      }
      viewModel.locations = model.locations;
      viewModel.anonym_code = model.anonym_code;
      viewModel.qrCodeUrl = model.qr_code_url;
      viewModel.qrCodeText = model.qr_code_text;
      await me._couponRedemptionService.ensureRedeemedCouponsLoaded();
      viewModel.isRedeemed = me._couponRedemptionService.isCouponRedeemed(model.id);
      viewModel.isRedeemOncePerUser = model.redeem_once_per_user != null ? model.redeem_once_per_user : false;

      return Promise.resolve(viewModel);
    } else {
      return Promise.resolve(null);
    }
  }

}
